import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { Hero } from '@cfm-media/pwa-ui-components';

import { ContentWrapper } from '../components/content-wrapper';
import { LoadableStatus } from '../mobx-utils';
import { Loader } from '../components/loader';
import { PageWrapper } from '../components/page-wrapper';
import { parseHtml } from '../core/utils';
import { serviceTeaserConfig } from '../utils/teasers';
import { useStore } from '../core/store';
import { useTranslation } from '../core/utils/translations';

export const Payment = observer(() => {
  const { BookingStore } = useStore();

  const [payment, setPayment] = useState({});

  useEffect(() => {
    if (BookingStore?.booking?.data?.payment) {
      setPayment(BookingStore.booking.data.payment);
    }
  }, [BookingStore?.booking?.status]);

  if (BookingStore?.booking?.status !== LoadableStatus.done) {
    return <Loader />;
  }

  return (
    <PageWrapper>
      <Hero
        image={serviceTeaserConfig.payment.image}
        iconClass="icon-payment"
        headline="Bezahlung"
        overlay
      />

      <ContentWrapper>
        <div className="hidden lg:block mb-3 text-xl text-brand-1 font-semibold uppercase">
          Zahlungsinformationen
        </div>

        {!payment.isInkasso ? (
          <div className="bg-white p-6 mb-3 relative rounded-md shadow-md">
            <div className="text-gray-900 font-regular leading-4">
              Bei Rückfragen zu den Zahlungsmodalitäten wenden Sie sich bitte an
              Ihr Reisebüro.
            </div>
          </div>
        ) : (
          <>
            <PaymentCards payment={{ ...payment }} />

            <PaymentLink payment={{ ...payment }} />

            <PaymentReference
              payment={{ ...payment }}
              bookingId={BookingStore.booking.data.id}
            />
          </>
        )}
      </ContentWrapper>
    </PageWrapper>
  );
});

export const PaymentCard = ({
  show = false,
  headline,
  amount,
  firstLine,
  secondLine,
  highLight,
  isPastDueDate = false,
  hideInfo = false,
  openAmount,
}) => {
  if (!show) {
    return null;
  }

  return (
    <div className="bg-white p-6 mb-3 relative rounded-md shadow-md">
      <div className="mb-2 text-gray-500 font-regular text-xs">{headline}</div>
      <div className="mb-2 text-brand-1 font-semibold text-4xl">{amount}</div>
      {hideInfo ? null : (
        <div className="text-gray-900 font-regular text-xs leading-4">
          {firstLine}
          <br />
          {secondLine}
        </div>
      )}
      <span
        className={classNames(
          {
            'bg-red-400/30': isPastDueDate && highLight === 'Überfällig',
            'bg-green-400/30': highLight === 'Bezahlt',
            'bg-lime-400/30': !isPastDueDate && highLight === 'Offen',
          },
          `px-3 py-1 inline-block text-xs ${
            isPastDueDate && highLight === 'Überfällig'
              ? 'text-red-700'
              : 'text-lime-700'
          } font-semibold absolute bottom-6 right-6 rounded-xl`,
        )}
      >
        {highLight}
      </span>
      {openAmount && (
        <div className="text-brand-1 font-semibold">
          {parseFloat(openAmount) >= 0
            ? `Offener Betrag: ${openAmount}`
            : `Guthaben: ${Math.abs(parseFloat(openAmount)).toFixed(2)}€`}
        </div>
      )}
    </div>
  );
};

const PaymentCards = ({ payment }) => {
  const currency = payment.currency === 'EUR' ? '€' : payment.currency;

  const currentDate = new Date();

  const [day, month, year] = payment.finalPaymentDate8.split('.');

  const dueDate = year + '.' + month + '.' + day;

  const isPastDueDate = currentDate > new Date(dueDate);

  const isFullyPaid = payment.open <= 0;

  const paymentState = isFullyPaid
    ? 'Bezahlt'
    : isPastDueDate
    ? 'Überfällig'
    : 'Offen';

  const missingAmount = payment.gross - payment.downpayment;

  const amountPaid = payment.gross - payment.open;

  return (
    <div className="lg:grid lg:grid-cols-2 lg:gap-10">
      <PaymentCard
        show
        headline="Anzahlung"
        amount={toCurrency(payment.downpayment, currency)}
        firstLine={`Zahlungsdatum: ${
          payment.downpaymentDate8 ? payment.downpaymentDate8 : 'N/A'
        }`}
        secondLine={`Zahlungsart: ${payment.paymentTypeText}`}
        highLight={payment.downpayment > amountPaid ? 'Offen' : 'Bezahlt'}
        // If the user hasn't payed the full downpayment, display what is left open of the downpayment (payment.downpayment - amountPaid)
        openAmount={
          amountPaid < payment.downpayment && amountPaid != 0
            ? toCurrency(payment.downpayment - amountPaid, currency)
            : null
        }
      />

      <PaymentCard
        show
        headline="Restzahlung"
        amount={toCurrency(missingAmount, currency)}
        firstLine={`Fälligkeitsdatum: ${payment.finalPaymentDate8}`}
        secondLine={`Zahlungsart: ${payment.paymentTypeText}`}
        highLight={paymentState}
        isPastDueDate={isPastDueDate}
        // If the user has payed more than the downpayment, but not the whole payment, show what's left to pay
        openAmount={
          amountPaid > payment.downpayment && payment.open != 0
            ? toCurrency(payment.open, currency)
            : null
        }
      />

      <PaymentCard
        show
        headline="Gesamtpreis"
        amount={toCurrency(payment.gross, currency)}
        firstLine={
          payment.open != 0
            ? ''
            : `Fälligkeitsdatum: ${payment.finalPaymentDate8}`
        }
        secondLine={`Zahlungsart: ${payment.paymentTypeText}`}
        highLight={paymentState}
        isPastDueDate={isPastDueDate && !isFullyPaid}
        hideInfo={payment.open != 0 && payment.downpayment != 0}
        //If the user hasn't paid anything yet, or the user paid everything, do not show the open payment field
        openAmount={
          amountPaid === 0 || payment.open === 0
            ? null
            : toCurrency(payment.open, currency)
        }
      />
    </div>
  );
};

const PaymentReference = ({ payment, bookingId }) => {
  const { t } = useTranslation('payment');

  return (
    <div className="bg-white p-6 mb-3 relative rounded-md shadow-md lg:my-10">
      <div className="mb-2 text-gray-500 font-regular text-xs">
        Bankverbindung
      </div>

      <div className="text-gray-900 font-regular text-md leading-4">
        {parseHtml(t(`bank-connection-${payment.currency?.toLowerCase()}`))}
        <br />
        <br />
        <div>
          Bitte überweisen Sie an eine der Bankverbindungen mit der
          Referenznummer <b>{payment.utr || bookingId}</b> als Verwendungszweck.
        </div>
      </div>
    </div>
  );
};

const PaymentLink = ({ payment }) => {
  const { t } = useTranslation('payment-page');

  if (!payment.link) {
    console.warn('No payment link found');
    return null;
  }

  return (
    <div className="bg-white p-6 mb-3 relative rounded-md shadow-md lg:my-7">
      <div className="mb-2 text-gray-500 font-regular text-xs">
        Jetzt bezahlen
      </div>

      <div className="text-gray-900 font-regular text-md leading-4">
        Sie möchten Ihre Reise ganz einfach per Kreditkarte bezahlen?
        <br />
        <br />
        Dann folgen Sie bitte{' '}
        <a
          className="text-brand-1 font-semibold cursor-pointer hover:underline"
          href={payment.link}
          target="_blank"
          rel="noopener noreferrer"
        >
          diesem Link
        </a>
        .
        <br />
        <br />
        <br />
        {parseHtml(t('credit-payment-text'))}
      </div>
    </div>
  );
};

function toCurrency(amount, currency) {
  return amount.toFixed(2) + currency;
}
